import { navigate } from "gatsby-link"
import React, { Fragment, useCallback, useEffect, useState } from "react"
import { Helmet } from "react-helmet"
import { Button, Col, Container, Modal, ModalBody, Row } from "reactstrap"
import { Link, SocialIcons } from ".."
import isoLanguage from "../../data/languages"
import FMMLogoMobile from "../../images/fmm-logo-mobile.svg"
import FMMLogoMobileWhite from "../../images/fmm-logo-mobile-white.svg"
import FMMLogoWhite from "../../images/fmm-logo-white.svg"
// import FMMLogo from "../../images/fmm-logo.svg"
import HamburgerCloseIcon from "../../images/hamburger-close-icon.svg"
import HamburgerIcon from "../../images/hamburger-icon.svg"
import LanguageIconColored from "../../images/language-icon-colored.svg"
import LanguageIcon from "../../images/language-icon.svg"
import "./Header.scss"
import HeaderMenu from "./HeaderMenu"
import HrefLangs from "./HrefLangs"
import BrandText from "../BrandText/BrandText"

const Header = ({ lang, languages, mobileTrigger, slug, copyrightMessage }) => {
  const [languageModal, setLanguageModal] = useState(false)
  const [menuModal, setMenuModal] = useState(false)
  const [selectedLanguageName, setSelectedLanguageName] = useState(lang)
  const [pageLanguages, setPageLanguages] = useState([])
  const [pagePathArray, setPagePathArray] = useState("")

  useEffect(() => {
    if (typeof window !== "undefined") {
      setPagePathArray(window?.location.pathname?.substring(1).split("/"))
    } else {
      setPagePathArray("/")
    }
  }, [])

  useEffect(() => {
    mobileTrigger(menuModal)
  }, [menuModal, mobileTrigger])

  const navigateToSelectedPath = value => {
    if (value === "en") {
      navigate(`/${pagePathArray.join(`/`)}`)
    } else {
      navigate(`/${value}/${pagePathArray.join(`/`)}`)
    }
  }

  const updateLanguage = e => {
    const { value } = e.target
    toggleLanguageModal()

    if (pagePathArray[0] === lang) {
      pagePathArray.shift()
      navigateToSelectedPath(value)
    } else {
      navigateToSelectedPath(value)
    }
  }

  const createPageLanguages = useCallback(() => {
    const langArr = []

    isoLanguage?.forEach(lang => {
      const langInArray = languages?.find(
        fmmLang => fmmLang?.node?.fmmCore?.languageCode === lang?.code
      )

      if (langInArray) {
        langArr.push({
          name: lang?.name,
          code: lang?.code,
        })
      }
    })

    setPageLanguages(langArr)
  }, [languages])

  useEffect(() => {
    const languageName = pageLanguages?.find(
      language => language.code === lang
    )?.name
    setSelectedLanguageName(languageName)
  }, [lang, pageLanguages])

  useEffect(() => {
    createPageLanguages()
  }, [languages, createPageLanguages])

  const toggleLanguageModal = () => setLanguageModal(!languageModal)
  const toggleMenuModal = () => setMenuModal(!menuModal)

  return (
    <>
      <Helmet>
        <meta
          name="facebook-domain-verification"
          content="5ek1kjuz6ciblskgfkjc2uxenp2re3"
        />
      </Helmet>
      <HrefLangs languages={languages} slug={slug} lang={lang} />

      <header
        id="header"
        className={`${menuModal ? "mobile-menu-active" : ""} ${lang}`}
      >
        <section className="container">
          <section className="row d-flex justify-content-between">
            <Col xs="6" md="3" className="d-flex align-items-center">
              <Link to="/" lang={lang}>
                <picture>
                  <source
                    media="(max-width: 767px)"
                    srcSet={menuModal ? FMMLogoMobile : FMMLogoMobileWhite}
                    height="40px"
                    width="120px"
                  />
                  <img
                    src={FMMLogoWhite}
                    alt="Find My Method"
                    height="60"
                    width="192"
                  />
                </picture>
              </Link>
            </Col>

            <Col xs="6" md="9" className="d-flex d-lg-none align-items-center">
              <section className="ml-auto">
                <button className="icon-button" onClick={toggleLanguageModal}>
                  <img
                    src={!menuModal ? LanguageIcon : LanguageIconColored}
                    height="22px"
                    width="22px"
                    alt=""
                  />
                </button>

                <button className="icon-button ml-3" onClick={toggleMenuModal}>
                  <img
                    src={!menuModal ? HamburgerIcon : HamburgerCloseIcon}
                    height="40"
                    width="40"
                    alt=""
                  />
                </button>
              </section>
            </Col>

            <div className="nav d-none d-lg-flex">
              <Button
                onClick={toggleLanguageModal}
                className="button button--small button--secondary"
              >
                <img
                  src={LanguageIcon}
                  width="100%"
                  loading="lazy"
                  className="ml-2"
                  alt="Language Selector"
                />
                <span className="lang-name">{selectedLanguageName}</span>
              </Button>
            </div>
          </section>
          <section className="">
            <nav className="nav main-menu d-none d-lg-flex">
              <HeaderMenu
                lang={lang}
                toggleLanguageModal={toggleLanguageModal}
                selectedLanguageName={selectedLanguageName}
              />
            </nav>
          </section>
        </section>
      </header>

      <Modal
        isOpen={languageModal}
        toggle={toggleLanguageModal}
        returnFocusAfterClose={false}
        className="language-selector"
      >
        <ModalBody>
          <Container>
            <section className="language__holder">
              {pageLanguages?.map((language, index) => (
                <Fragment key={index}>
                  {language.code !== lang && (
                    <button
                      onClick={updateLanguage}
                      className="language__item"
                      value={language.code}
                      name={language.name}
                    >
                      {language.name}
                    </button>
                  )}
                </Fragment>
              ))}
            </section>
          </Container>
        </ModalBody>
      </Modal>

      {/* Mobile */}
      <section className={`mobile-menu ${menuModal ? "active" : ""}`}>
        <nav className="menu">
          <HeaderMenu
            lang={lang}
            toggleLanguageModal={toggleLanguageModal}
            selectedLanguageName={selectedLanguageName}
          />
        </nav>

        <section className="mt-auto pb-5">
          <Row className="d-flex align-items-center">
            <section className="col-12 text-center">
              <BrandText />
            </section>

            <section className="col-12">
              <SocialIcons mobileMenuOpen={true} />
            </section>

            <section className="col-12">
              <p className="footer__copyright">
                © {new Date().getFullYear()} {copyrightMessage}
              </p>
            </section>
          </Row>
        </section>
      </section>
    </>
  )
}

export default Header
